// src/pages/CompanyForm.js
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getCompanyById, createCompany, updateCompany } from '../services/CompanyServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import { Country } from "country-state-city";
import styles from "./Table.module.css";
import { getUsersByCompanyId, deleteUser } from '../services/UserServices';
import { getSellersByProject, deleteSeller } from '../services/SellerService';
import { getRolesByCompanyId, deleteRole } from '../services/RoleServices';
import UserInvitePanel from './UserInvitePanel';
import RolesForm from './RolesForm';

import './Forms.css';
import "./ProjectForm.css";

function CompanyForm() {
  const { id } = useParams();
  const [company, setCompany] = useState({ name: '', country: '', slug: '' });
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isRoleModalOpen, setRoleModalOpen] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteRoleModal, setShowDeleteRoleModal] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const navigate = useNavigate();

  const fetchUsers = async (companyId) => {
    try {
      const fetchedUsers = await getUsersByCompanyId(companyId);
      setUsers(fetchedUsers || []);
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchRoles = async (companyId) => {
    try {
      const response = await getRolesByCompanyId(companyId);
      const fetchedRoles = response.data || response || [];
      setRoles(Array.isArray(fetchedRoles) ? fetchedRoles : []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError(error.message);
    }
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const allCountries = Country.getAllCountries();
        setCountries(allCountries);
        if (id) {
          const companyData = await getCompanyById(id);
          setCompany(companyData);

          const fetchedUsers = await getUsersByCompanyId(companyData.id);
          setUsers(fetchedUsers || []);

          const rolesResponse = await getRolesByCompanyId(companyData.id);
          const fetchedRoles = rolesResponse.data || rolesResponse || [];
          setRoles(Array.isArray(fetchedRoles) ? fetchedRoles : []);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  const handleNameChange = (e) => {
    const newName = e.target.value;
    const newSlug = newName.toLowerCase().replace(/\s+/g, '-'); // Generar el slug a partir del nombre
    setCompany({ ...company, name: newName, slug: newSlug });
  };

  const handleUserInvited = (newUser) => {
    setUsers((prevUsers) => [...prevUsers, newUser]);
    setInviteModalOpen(false);
  };

  const handleRoleSaved = async () => {
    if (company.id) {
      await fetchRoles(company.id);
    }
  };

  const openRoleModal = (role = null) => {
    setRoleToEdit(role);
    setRoleModalOpen(true);
  };

  const closeRoleModal = () => {
    setRoleToEdit(null);
    setRoleModalOpen(false);
  };

  const openDeleteModal = (userId) => {
    setUserToDelete(userId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const openDeleteRoleModal = (roleId) => {
    setRoleToDelete(roleId);
    setShowDeleteRoleModal(true);
  };

  const closeDeleteRoleModal = () => {
    setShowDeleteRoleModal(false);
    setRoleToDelete(null);
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;
    try {
      setLoading(true);
      await deleteRole(roleToDelete);
      setRoles(prevRoles => prevRoles.filter(role => role.id !== roleToDelete));
      setError(null);
      alert('Rol eliminado correctamente');
    } catch (error) {
      setError(`Error al eliminar rol: ${error.message}`);
      await fetchRoles(company.id);
    } finally {
      setLoading(false);
      closeDeleteRoleModal();
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      setLoading(true);
      setDeletingId(userToDelete);

      const userExists = users.find(user => user.id === userToDelete);
      if (!userExists) throw new Error('El usuario no existe en la lista actual');

      try {
        const userProjects = userExists.projects || [];
        const allSellers = [];

        for (const project of userProjects) {
          const sellersResponse = await getSellersByProject(project.id);
          const projectSellers = sellersResponse.data || sellersResponse;
          if (Array.isArray(projectSellers)) {
            allSellers.push(...projectSellers);
          }
        }

        const sellersToDelete = allSellers.filter(seller => seller.email === userExists.email);

        if (sellersToDelete.length > 0) {
          await Promise.all(sellersToDelete.map(seller => deleteSeller(seller.id)));
        }
      } catch (sellerError) {
        console.error('Error al eliminar sellers:', sellerError);
      }

      await deleteUser(userToDelete);

      setUsers(prevUsers => prevUsers.filter(user => user.id !== userToDelete));
      setError(null);
      setDeletingId(null);
      alert('Usuario eliminado correctamente');
    } catch (error) {
      setError(`Error al eliminar usuario: ${error.message}`);
      await fetchUsers(company.id);
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await updateCompany(id, company);
      } else {
        await createCompany(company);
      }
      navigate('/companies');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  /*
  const handleDeleteUser = async (userId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        setLoading(true);
        setDeletingId(userId);

        const userToDelete = users.find(user => user.id === userId);
        if (!userToDelete) {
          throw new Error('El usuario no existe en la lista actual');
        }

        await deleteUser(userId);

        setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
        setError(null);
        setDeletingId(null);
      } catch (error) {
        console.error('Error al eliminar usuario:', error);
        setError(`Error al eliminar usuario: ${error.message}`);

        await fetchUsers(company.id);
      } finally {
        setLoading(false);
      }
    }
  };
  */

  if (loading) {
    return <p>Cargando...</p>;
  }

  return (
    <div className="page-container">
      <div className="form-container">
        {error && (
          <div>
            <div className='error-message'>
              Hubo un error: {error}
            </div>
            <br />
          </div>
        )}
        <h2>{id ? 'Editar Compañía' : 'Nueva Compañía'}</h2>
        <form onSubmit={handleSubmit} className="company-form">
          <div className="form-group">
            <label htmlFor="company-name">Nombre</label>
            <input
              type="text"
              id="company-name"
              value={company.name}
              onChange={handleNameChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="company-country">País</label>
            <select
              id="company-country"
              value={company.country}
              onChange={(e) => setCompany({ ...company, country: e.target.value })}
              required
            >
              <option value="">Seleccione un país</option>
              {countries.map((country) => (
                <option key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="company-slug">Slug (auto-generado)</label>
            <input
              type="text"
              id="company-slug"
              value={company.slug}
              readOnly
            />
          </div>

          <div className="form-group">
            <button type="submit" className="btn-submit">{id ? 'Guardar Cambios' : 'Agregar Compañía'}</button>
          </div>
        </form>
        <div className="form-footer">
          <Link to="/companies" className="back-link">Regresar a Compañías</Link>
        </div>
      </div>

      <div className="stages-container">
        <div className="sections-container">
          <h2>Roles</h2>
          <button className='btn-add' onClick={() => openRoleModal()}>
            <FontAwesomeIcon icon={faPlus} />
          </button>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nombre del Cargo</th>
                <th>Descripción</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>{role.name}</td>
                  <td>{role.description}</td>
                  <td>
                    <div className="actions-container">
                      <button
                        onClick={() => openRoleModal(role)}
                        className="btn-edit"
                        style={{ marginRight: '10px' }}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => openDeleteRoleModal(role.id)}
                        className="btn-delete">
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sections-container">
          <h2>Usuarios</h2>
          <button className='btn-add' onClick={() => setInviteModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} />
          </button>

          <table className={styles.table}>
            <thead>
              <tr>
                <th> Nombre</th>
                <th style={{ textAlign: 'center' }}> Email </th>
                <th> Proyectos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td> {user.firstName} {user.lastName}</td>
                  <td>{user.email}</td>
                  <td style={{ textAlign: 'center' }}>
                    {Array.isArray(user.projects) && user.projects.length > 0 ? (
                      user.projects.map((project) => (
                        <span key={project.id} className={styles.projectBadge}>
                          {project.name}
                        </span>
                      ))
                    ) : (
                      <span>No asignado</span>
                    )}
                  </td>
                  <td>
                    <div className="actions-container">
                      <button
                        onClick={() => openDeleteModal(user.id)}
                        disabled={deletingId === user.id}
                        className="btn-delete"
                        style={{ marginLeft: '10px' }}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isInviteModalOpen && (
          <div className="modal-backdrop">
            <div className="modal-content-user">
              <button className="modal-close" onClick={() => setInviteModalOpen(false)}>×</button>
              <UserInvitePanel
                companyId={company.id}
                onClose={() => setInviteModalOpen(false)}
                onUserInvited={handleUserInvited}
              />
            </div>
          </div>
        )}

        {showDeleteModal && (
          <div className="media-modal-overlay" onClick={closeDeleteModal}>
            <div className="media-modal" onClick={e => e.stopPropagation()}>
              <h3 className="modal-content-h3">¿Seguro que deseas eliminar este usuario?</h3>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                <button className="btn-submit" onClick={confirmDelete}>Aceptar</button>
                <button className="btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {isRoleModalOpen && (
          <div className="modal-backdrop">
            <div className="modal-content-user">
              <button className="modal-close" onClick={closeRoleModal}>×</button>
              <RolesForm
                companyId={company.id}
                onClose={closeRoleModal}
                onRoleSaved={handleRoleSaved}
                roleToEdit={roleToEdit}
              />
            </div>
          </div>
        )}

        {showDeleteRoleModal && (
          <div className="media-modal-overlay" onClick={closeDeleteRoleModal}>
            <div className="media-modal" onClick={e => e.stopPropagation()}>
              <h3 className="modal-content-h3">¿Seguro que deseas eliminar este rol?</h3>
              <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                <button className="btn-submit" onClick={confirmDeleteRole}>Aceptar</button>
                <button className="btn-cancel" onClick={closeDeleteRoleModal}>Cancelar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyForm;
